/**
 * National AI Strategies — country-level investment plans + sovereign-AI moves.
 * /api/frontier/national-strategies
 */
import { useEffect, useMemo, useState } from 'react'
import { Globe, RefreshCw, ExternalLink, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Strategy {
  id: string
  country: string
  region: string
  announced: string
  title: string
  scope: string
  lead_org: string
  summary: string
  url: string
}

const REGION_TONE: Record<string, string> = {
  'Americas':    'bg-blue/15 text-blue-light',
  'Europe':      'bg-emerald/15 text-emerald',
  'Asia':        'bg-rose/15 text-rose-light',
  'Middle East': 'bg-amber/15 text-amber-light',
}

const FLAG: Record<string, string> = {
  US: '🇺🇸', GB: '🇬🇧', EU: '🇪🇺', FR: '🇫🇷', DE: '🇩🇪',
  CN: '🇨🇳', IN: '🇮🇳', JP: '🇯🇵', KR: '🇰🇷', SG: '🇸🇬',
  AE: '🇦🇪', SA: '🇸🇦', CA: '🇨🇦', IL: '🇮🇱',
}

export default function NationalStrategiesPage() {
  const [rows, setRows] = useState<Strategy[]>([])
  const [regions, setRegions] = useState<string[]>([])
  const [regionFilter, setRegionFilter] = useState('')
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/national-strategies')
      .then(r => {
        setRows(r.data?.strategies || [])
        setRegions(r.data?.regions || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() =>
    regionFilter ? rows.filter(s => s.region === regionFilter) : rows
  , [rows, regionFilter])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Globe size={18} className="text-blue-light" /> NATIONAL AI STRATEGIES
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          country-level plans · sovereign-ai investment · regulators
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Strategies" value={rows.length} tone="blue" />
        <Tile label="Regions" value={regions.length} tone="emerald" />
        <Tile label="Countries" value={new Set(rows.map(s => s.country)).size} tone="amber" />
        <Tile label="Filtered" value={filtered.length} tone="purple" />
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={regionFilter} onChange={e => setRegionFilter(e.target.value)}
          aria-label="Filter by region">
          <option value="">All regions</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(s => (
          <article key={s.id} className="card p-4">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-2xl shrink-0">{FLAG[s.country] || '🌐'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-text-muted">{s.country} · {s.region}</div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">{s.title}</h3>
              </div>
              <span className={clsx('badge text-[9px] shrink-0', REGION_TONE[s.region] || 'bg-surface-raised text-text-muted')}>
                {s.scope}
              </span>
            </div>
            <div className="text-[10px] text-text-muted font-mono mb-2">
              announced {s.announced} · {s.lead_org}
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">{s.summary}</p>
            <a href={s.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          </article>
        ))}
      </div>
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone: 'blue' | 'emerald' | 'amber' | 'purple' }) {
  const c = tone === 'blue' ? 'text-blue-light'
          : tone === 'emerald' ? 'text-emerald'
          : tone === 'amber' ? 'text-amber-light'
          : 'text-purple-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
